import React, {
  Dispatch,
  HTMLAttributes,
  KeyboardEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { WidgetProps } from '@rjsf/utils';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import dayjs from '~/timeInit';

import AttractionsOutlinedIcon from '@mui/icons-material/AttractionsOutlined';
import DirectionsBoatOutlinedIcon from '@mui/icons-material/DirectionsBoatOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { Box, CircularProgress, FormControl, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { debounce } from '@mui/material/utils';

import { getExperienceOfferById } from '~/services/ExperiencesService';
import { getLuxLoyaltyAccountByNumber } from '~/services/LuxLoyaltyService';
import OffersService from '~/services/OffersService';
import * as OrdersService from '~/services/OrdersService';
import PublicOfferService from '~/services/PublicOfferService';
import UsersService from '~/services/UsersService';
import CruiseOfferService from '~/services/cruises/OfferService';

import type { UserType } from '~/types/responses';

import { getToursSellingRegions } from '~/utils/tourV2';

type OfferVertical = 'hotel' | 'tour' | 'experience' | 'cruise';

export type SearchResult =
  | {
      entityType: 'user';
      value: string;
      label: string;
      email: string;
      fullName?: string;
      id?: string;
    }
  | {
      entityType: 'order';
      value: string;
      label: string;
      orderNumber?: string;
      customerName?: string;
      customerEmail?: string;
      status?: string;
      createdAt?: string;
      brand?: string;
      id?: string;
    }
  | {
      entityType: 'offer';
      value: string;
      label: string;
      name?: string;
      brand?: string;
      offerType?: string;
      status?: string;
      vertical?: OfferVertical;
      verticalLabel?: string;
      targetPath: string;
      vendorName?: string;
      id?: string;
    };

interface Props extends Omit<WidgetProps, 'onChange' | 'onBlur'> {
  formErrors: Array<string>;
  onChange: (id: string, value: string) => void;
  loading?: boolean;
  passedAutocompleteValue?: string;
  setPassedAutocompleteValue?: Dispatch<SetStateAction<string>>;
  clearOnBlur?: boolean;
  showOnlyEmail?: boolean;
  includeOrders?: boolean;
  includeOffers?: boolean;
  onSelectResult?: (result: SearchResult | null) => void;
}

type UserSuggestion = SearchResult & {
  entityType: 'user';
  user: UserType;
};

type OrderSuggestion = SearchResult & {
  entityType: 'order';
};

type OfferSuggestion = SearchResult & {
  entityType: 'offer';
};

type SearchSuggestion = UserSuggestion | OrderSuggestion | OfferSuggestion;

const SALESFORCE_ID_REGEX = /^[0-9a-zA-Z]{18}$/;
const UUID_REGEX = /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/;
const EXPERIENCE_COMPOSITE_ID_REGEX = /^[A-Za-z]{3}:[^:]+$/;
const ALPHANUMERIC_WITH_SPECIAL = /^[A-Za-z0-9:-]+$/;

const OFFER_VERTICAL_LABEL_MAP: Record<OfferVertical, string> = {
  hotel: 'Hotel',
  tour: 'Tour',
  experience: 'Experience',
  cruise: 'Cruise',
};

const OFFER_ICON_MAP: Record<OfferVertical, typeof HotelOutlinedIcon> = {
  hotel: HotelOutlinedIcon,
  tour: MapOutlinedIcon,
  experience: AttractionsOutlinedIcon,
  cruise: DirectionsBoatOutlinedIcon,
};

const OFFER_TYPE_TO_VERTICAL: Record<string, OfferVertical> = {
  hotel: 'hotel',
  last_minute_hotel: 'hotel',
  tactical_ao_hotel: 'hotel',
  bundle_and_save: 'hotel',
  rental: 'hotel',
  bedbank_hotel: 'hotel',
  flash: 'hotel',
  flash_sale: 'hotel',
  custom_offer: 'hotel',
  merchandise: 'hotel',
  tour: 'tour',
  tour_v2: 'tour',
  connection_tour: 'tour',
  partner_tour: 'tour',
  direct_tour: 'tour',
  ultra_lux_tour: 'tour',
  experience: 'experience',
  cruise: 'cruise',
  cruise_v2: 'cruise',
};

const REGION_FALLBACKS = ['AU', 'NZ', 'US', 'SG', 'GB'];

const isSalesforceId = (value: string) => SALESFORCE_ID_REGEX.test(value);
const isUuid = (value: string) => UUID_REGEX.test(value);
const isExperienceCompositeId = (value: string) => EXPERIENCE_COMPOSITE_ID_REGEX.test(value);
const isTourOfferIdFormat = (value: string) => value.startsWith('tour-');
const isExperienceOfferIdFormat = (value: string) => value.toLowerCase().startsWith('led:');

function sanitizePhoneNumber(input: string): string {
  return input.trim().replace(/^\+|\s+/g, '');
}

function isPhoneNumber(input: string): boolean {
  const sanitized = sanitizePhoneNumber(input);
  return /^\d+$/.test(sanitized);
}

const isPotentialOfferId = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return false;
  }

  if (/\s/.test(trimmed)) {
    return false;
  }

  if (trimmed.includes('@')) {
    return false;
  }

  if (!ALPHANUMERIC_WITH_SPECIAL.test(trimmed)) {
    return false;
  }

  if (isSalesforceId(trimmed) || isUuid(trimmed) || isExperienceCompositeId(trimmed) || isTourOfferIdFormat(trimmed)) {
    return true;
  }

  return trimmed.length >= 16;
};

const mapOfferTypeToVertical = (offerType?: string): OfferVertical | undefined => {
  if (!offerType) {
    return undefined;
  }

  const direct = OFFER_TYPE_TO_VERTICAL[offerType];
  if (direct) {
    return direct;
  }

  const normalized = offerType.toLowerCase();

  if (normalized.includes('cruise') || normalized === 'ship' || normalized === 'sailing') {
    return 'cruise';
  }

  if (normalized.includes('tour') || normalized.includes('ttc') || normalized.includes('lux_plus_tour')) {
    return 'tour';
  }

  if (normalized.includes('experience') || normalized.includes('exp')) {
    return 'experience';
  }

  if (
    normalized.includes('hotel') ||
    normalized.includes('flash') ||
    normalized.includes('bundle') ||
    normalized.includes('rental') ||
    normalized.includes('custom') ||
    normalized.includes('merch')
  ) {
    return 'hotel';
  }

  return undefined;
};

const getVerticalLabel = (vertical?: OfferVertical): string | undefined => {
  if (!vertical) {
    return undefined;
  }

  return OFFER_VERTICAL_LABEL_MAP[vertical];
};

const getOfferIconComponent = (vertical?: OfferVertical) => {
  if (!vertical) {
    return undefined;
  }

  return OFFER_ICON_MAP[vertical];
};

const getOfferTargetPath = (id: string, offerType?: string, vertical?: OfferVertical): string => {
  if (offerType === 'bedbank_hotel') {
    return `/bedbank/properties/${id}`;
  }

  if (vertical === 'cruise') {
    return `/cruises/offer/${id}`;
  }

  if (vertical === 'experience') {
    return `/experience/${id}`;
  }

  if (vertical === 'tour') {
    if (offerType === 'tour_v2') {
      return `/tours/details/${id}`;
    }
    return `/offers/${id}`;
  }

  return `/offers/${id}`;
};

function UserSearchWidget({
  id,
  required,
  readonly,
  disabled,
  label,
  onChange,
  formErrors,
  loading,
  passedAutocompleteValue = null,
  setPassedAutocompleteValue,
  clearOnBlur = false,
  showOnlyEmail = false,
  includeOrders = false,
  includeOffers = false,
  onSelectResult,
}: Props) {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const { enqueueSnackbar } = useSnackbar();

  const [hoveredUserId, setHoveredUserId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [userCache, setUserCache] = useState<Record<string, UserType>>({});

  const debouncedSearchQuery = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    [],
  );

  const loadOfferSuggestions = useCallback(
    async (rawId: string): Promise<Array<OfferSuggestion>> => {
      const id = rawId.trim();

      if (!id) {
        return [];
      }

      const suggestions: Array<OfferSuggestion> = [];
      const seen = new Set<string>();

      const pushSuggestion = (suggestion: OfferSuggestion | null | undefined) => {
        if (suggestion && !seen.has(suggestion.value)) {
          seen.add(suggestion.value);
          suggestions.push(suggestion);
        }
      };

      const handleError = (error: any, context: string) => {
        if (!error) {
          return;
        }

        if (error?.name === 'AbortError') {
          return;
        }

        const status = error?.status ?? error?.response?.status;
        if (status === 400 || status === 404) {
          return;
        }

        enqueueSnackbar(`There was an error while fetching ${context} [${JSON.stringify(error, null, 2)}]`, {
          variant: 'error',
        });
      };

      const attempt = async (fn: () => Promise<OfferSuggestion | null>, context: string) => {
        try {
          const suggestion = await fn();
          pushSuggestion(suggestion);
        } catch (error) {
          handleError(error, context);
        }
      };

      const attempts: Array<() => Promise<void>> = [];

      const trySalesforceOffer = () =>
        attempt(async () => {
          try {
            const response = await OffersService.getOffer(id, brand ? { brand } : undefined);
            const offer = response?.result as App.Offer | undefined;

            if (!offer) {
              return null;
            }

            const vertical = mapOfferTypeToVertical(offer.type);
            if (!vertical) {
              return null;
            }

            const verticalLabel = getVerticalLabel(vertical);
            const status =
              (offer as any)?.status ??
              (offer as any)?.offer_status ??
              (offer as any)?.curation_status ??
              (offer as any)?.state ??
              undefined;
            const offerBrand = (offer as any)?.brand ?? (offer as any)?.selling_brand ?? (offer as any)?.brand_code;
            const vendorName =
              (offer as any)?.vendor_name ??
              (offer as any)?.vendor?.name ??
              (offer as any)?.vendorName ??
              (offer as any)?.property?.name ??
              undefined;
            const label = (offer.name && offer.name.trim().length > 0 ? offer.name : undefined) ?? id;

            return {
              entityType: 'offer' as const,
              value: id,
              label,
              id,
              name: offer.name,
              brand: offerBrand,
              status,
              offerType: offer.type,
              vertical,
              verticalLabel,
              targetPath: getOfferTargetPath(id, offer.type, vertical),
              vendorName,
            };
          } catch (error: any) {
            if (error?.status === 404) {
              return null;
            }
            throw error;
          }
        }, 'offers');

      const tryExperienceOffer = () =>
        attempt(async () => {
          if (!brand) {
            return null;
          }

          try {
            const response = await getExperienceOfferById({ id, brand });
            const experience = response?.result;

            if (!experience) {
              return null;
            }

            const experienceBrand = experience.brands?.[0] ?? brand;
            const status = experience.curationStatus ?? (experience as any)?.status;
            const vertical: OfferVertical = 'experience';
            const verticalLabel = getVerticalLabel(vertical);
            const vendorName = experience.vendor?.name ?? experience.provider?.[0] ?? undefined;
            const label = (experience.title && experience.title.trim().length > 0 ? experience.title : undefined) ?? id;

            return {
              entityType: 'offer' as const,
              value: id,
              label,
              id,
              name: experience.title,
              brand: experienceBrand,
              status,
              offerType: 'experience',
              vertical,
              verticalLabel,
              targetPath: `/experience/${id}`,
              vendorName,
            };
          } catch (error: any) {
            if (error?.status === 404) {
              return null;
            }
            throw error;
          }
        }, 'experience offers');

      const tryCruiseOffer = () =>
        attempt(async () => {
          try {
            const response = await CruiseOfferService.getSummaryById(id);
            const cruise = response?.result;

            if (!cruise) {
              return null;
            }

            const vertical: OfferVertical = 'cruise';
            const verticalLabel = getVerticalLabel(vertical);
            const vendorName = cruise.vendor?.name ?? undefined;
            const label = (cruise.name && cruise.name.trim().length > 0 ? cruise.name : undefined) ?? id;

            return {
              entityType: 'offer' as const,
              value: id,
              label,
              id,
              name: cruise.name,
              status: cruise.status,
              offerType: 'cruise',
              vertical,
              verticalLabel,
              targetPath: `/cruises/offer/${id}`,
              vendorName,
            };
          } catch (error: any) {
            if (error?.status === 404) {
              return null;
            }
            throw error;
          }
        }, 'cruise offers');

      const tryTourOffer = () =>
        attempt(async () => {
          if (!brand) {
            return null;
          }

          const tourRegions = (() => {
            try {
              return getToursSellingRegions();
            } catch {
              return [];
            }
          })();

          const regionsToTry = tourRegions?.length ? tourRegions : REGION_FALLBACKS;

          for (const region of regionsToTry) {
            try {
              const response = await PublicOfferService.getTourV2OfferById(id, region, brand);
              const tour = response?.result;

              if (!tour) {
                continue;
              }

              const vertical: OfferVertical = 'tour';
              const verticalLabel = getVerticalLabel(vertical);
              const vendorName =
                (tour as any)?.brandObject?.name ?? (tour as any)?.operator?.name ?? tour.brand ?? undefined;
              const label = (tour.name && tour.name.trim().length > 0 ? tour.name : undefined) ?? id;

              return {
                entityType: 'offer' as const,
                value: id,
                label,
                id,
                name: tour.name,
                brand: tour.brand ?? brand,
                status: (tour as any)?.status,
                offerType: tour.type ?? 'tour_v2',
                vertical,
                verticalLabel,
                targetPath: `/tours/details/${id}`,
                vendorName,
              };
            } catch (error: any) {
              if (error?.status === 404) {
                continue;
              }
              throw error;
            }
          }

          return null;
        }, 'tour offers');

      if (isSalesforceId(id)) {
        attempts.push(trySalesforceOffer);
      }

      if (isExperienceOfferIdFormat(id)) {
        attempts.push(tryExperienceOffer);
      }

      if (isTourOfferIdFormat(id)) {
        attempts.push(tryTourOffer);
      }

      if (isUuid(id)) {
        attempts.push(tryCruiseOffer);
      }

      if (!attempts.length) {
        // fallback attempts when format is unknown
        attempts.push(trySalesforceOffer);
        attempts.push(tryExperienceOffer);
      }

      for (const runAttempt of attempts) {
        if (seen.has(id)) {
          break;
        }
        await runAttempt();
      }

      return suggestions;
    },
    [brand, enqueueSnackbar],
  );

  // user search query
  const { data: userSuggestions = [], isLoading } = useQuery({
    queryKey: ['users', 'search', searchQuery, brand],
    queryFn: async ({ signal }) => {
      try {
        let filterQuery = searchQuery;
        if (isPhoneNumber(searchQuery)) {
          filterQuery = sanitizePhoneNumber(searchQuery);
          if (filterQuery.startsWith('0')) {
            filterQuery = filterQuery.substring(1);
          }
        }

        // If query looks like a loyalty account number (digits >= 1), try lookup by societe first
        const looksLikeLoyaltyNumber = /^\d+$/.test(filterQuery) && Number(filterQuery) >= 1;
        if (looksLikeLoyaltyNumber) {
          const resolvedUsersById = new Map<string, UserType>();
          const pushUser = (user: UserType | null | undefined) => {
            if (user?.id_member) {
              resolvedUsersById.set(user.id_member, user);
            }
          };

          const resolveUsersByCustomerIds = async (customerIds: Array<string>) => {
            const uniqueIds = Array.from(
              new Set(customerIds.filter((value) => typeof value === 'string' && value.length > 0)),
            );
            if (uniqueIds.length === 0) {
              return;
            }

            const resolvedUsers = await Promise.all(
              uniqueIds.map(async (customerId) => {
                try {
                  return await UsersService.getUser(customerId, { brand, signal });
                } catch {
                  return null;
                }
              }),
            );
            resolvedUsers.forEach((user) => pushUser(user));
          };

          try {
            // accountNumber path
            const accountByNumber = await getLuxLoyaltyAccountByNumber(filterQuery);
            const customerIds = [accountByNumber?.customerId].filter(
              (value): value is string => typeof value === 'string' && value.length > 0,
            );

            if (customerIds.length > 0) {
              await resolveUsersByCustomerIds(customerIds);
            }
          } catch {
            // Swallow errors and fall through to normal search
          }

          if (resolvedUsersById.size > 0) {
            return Array.from(resolvedUsersById.values());
          }
        }

        const suggestions = await UsersService.getUsers(
          {
            filter: filterQuery,
            brand: brand,
          },
          { signal },
        );
        return suggestions;
      } catch (e) {
        // ignore abort errors
        if (e.name === 'AbortError') {
          return;
        }
        enqueueSnackbar(`There was an error while fetching users [${JSON.stringify(e, null, 2)}]`, {
          variant: 'error',
        });
        throw e;
      }
    },
    enabled: !!searchQuery && searchQuery.length >= 2,
  });

  const { data: orderSuggestions = [], isLoading: isOrdersLoading } = useQuery({
    queryKey: ['orders', 'search', searchQuery],
    queryFn: async ({ signal }) => {
      try {
        const normalizedQuery = searchQuery.trim();

        const suggestions = await OrdersService.searchPurchases(
          {
            page: 0,
            perPage: 5,
            searchId: normalizedQuery,
            orderDirection: 'desc',
            orderBy: 'created_at',
          },
          { signal },
        );

        return suggestions?.result ?? [];
      } catch (e) {
        if (e.name === 'AbortError') {
          return;
        }

        enqueueSnackbar(`There was an error while fetching orders [${JSON.stringify(e, null, 2)}]`, {
          variant: 'error',
        });
        throw e;
      }
    },
    enabled: includeOrders && !!searchQuery && searchQuery.length >= 2,
  });

  const trimmedSearchQuery = useMemo(() => searchQuery.trim(), [searchQuery]);
  const shouldFetchOffers = includeOffers && isPotentialOfferId(trimmedSearchQuery);

  const { data: offerSuggestions = [], isLoading: isOffersLoading } = useQuery({
    queryKey: ['offers', 'search', trimmedSearchQuery, brand],
    queryFn: async () => {
      return await loadOfferSuggestions(trimmedSearchQuery);
    },
    enabled: shouldFetchOffers,
  });

  const userOptions: Array<UserSuggestion> = useMemo(
    () =>
      userSuggestions.map((user) => ({
        entityType: 'user' as const,
        value: user.id_member,
        label: user.email,
        email: user.email,
        fullName: user.fullName,
        user,
      })),
    [userSuggestions],
  );

  const orderOptions: Array<OrderSuggestion> = useMemo(() => {
    if (!includeOrders) {
      return [];
    }

    return orderSuggestions.map((order: App.Order) => {
      const orderNumber = order.number || order.id;
      const customerName = order.customer_full_name;
      const customerEmail = order.customer_email;
      const createdAtLabel = order.created_at ? dayjs(order.created_at).format('DD MMM YYYY') : '';

      const labelParts = [`Order ${orderNumber}`];
      if (customerName) {
        labelParts.push(customerName);
      }
      if (customerEmail) {
        labelParts.push(customerEmail);
      }
      if (createdAtLabel) {
        labelParts.push(createdAtLabel);
      }

      const label = labelParts.join(' | ');

      return {
        entityType: 'order' as const,
        value: order.id,
        label,
        orderNumber: order.number,
        customerName: order.customer_full_name,
        customerEmail: order.customer_email,
        status: order.status,
        createdAt: order.created_at,
        brand: order.brand,
      };
    });
  }, [includeOrders, orderSuggestions]);

  const offerOptions: Array<OfferSuggestion> = useMemo(
    () => (includeOffers ? offerSuggestions : []),
    [includeOffers, offerSuggestions],
  );

  const options: Array<SearchSuggestion> = useMemo(
    () => [...offerOptions, ...userOptions, ...orderOptions],
    [offerOptions, userOptions, orderOptions],
  );

  // User details query
  const { data: userDetails } = useQuery({
    queryKey: ['user', 'details', hoveredUserId],
    queryFn: async () => {
      return await UsersService.getUser(hoveredUserId);
    },
    enabled: !!hoveredUserId && !userCache[hoveredUserId],
  });

  useEffect(() => {
    if (!passedAutocompleteValue) {
      setSearchQuery('');
      setInputValue('');
    } else {
      setInputValue(passedAutocompleteValue);
    }
  }, [passedAutocompleteValue]);

  const onInputChange = useCallback(
    (_: unknown, value: string) => {
      const sanitizedValue = value.replace(/^mailto:/i, '');
      setInputValue(sanitizedValue);
      if (sanitizedValue) {
        debouncedSearchQuery(sanitizedValue);
      } else {
        setSearchQuery('');
      }
    },
    [debouncedSearchQuery],
  );

  const toResult = useCallback((option: SearchSuggestion): SearchResult => {
    if (option.entityType === 'user') {
      const { user: _user, ...userResult } = option;
      return userResult;
    }

    return option;
  }, []);

  const handleOptionSelect = useCallback(
    (option: SearchSuggestion) => {
      if (option.entityType === 'user') {
        const displayValue = option.user.email;
        onChange(id, option.value);
        if (setPassedAutocompleteValue) {
          setPassedAutocompleteValue(displayValue);
        }
        setInputValue(displayValue);
      } else {
        const displayValue = option.label;
        onChange(id, option.value);
        if (setPassedAutocompleteValue) {
          setPassedAutocompleteValue(displayValue);
        }
        setInputValue(displayValue);
      }

      onSelectResult?.(toResult(option));
    },
    [id, onChange, onSelectResult, setPassedAutocompleteValue, setInputValue, toResult],
  );

  const onEnterPress = useCallback(
    async (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') {
        return;
      }

      const currentInput = inputValue.trim();
      if (!currentInput.length) {
        return;
      }

      setSearchQuery(currentInput);

      if (options.length === 1) {
        handleOptionSelect(options[0]);
      }
    },
    [handleOptionSelect, inputValue, options, setSearchQuery],
  );

  // Update cache when user details are fetched
  useEffect(() => {
    if (userDetails && hoveredUserId) {
      setUserCache((prev) => ({ ...prev, [hoveredUserId]: userDetails }));
    }
  }, [userDetails, hoveredUserId]);

  const handleOptionHover = useCallback(
    (option: SearchSuggestion) => {
      if (option.entityType !== 'user') {
        setHoveredUserId(null);
        return;
      }

      if (!userCache[option.value]) {
        setHoveredUserId(option.value);
      }
    },
    [userCache, setHoveredUserId],
  );

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: SearchSuggestion) => {
      if (option.entityType === 'offer') {
        const OfferIcon = getOfferIconComponent(option.vertical);
        const vendorDisplay = option.vendorName ?? (option.brand ? option.brand.toUpperCase() : '');

        return (
          <li {...props} onMouseEnter={() => handleOptionHover(option)} style={{ cursor: 'pointer' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {OfferIcon ? <OfferIcon fontSize="small" color="action" /> : null}
                <Typography fontWeight="bold">{option.label}</Typography>
              </Box>
              {vendorDisplay ? (
                <Typography color="text.secondary" variant="body2">
                  {vendorDisplay}
                </Typography>
              ) : null}
            </Box>
          </li>
        );
      }

      if (option.entityType === 'order') {
        return (
          <li {...props} onMouseEnter={() => handleOptionHover(option)} style={{ cursor: 'pointer' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography fontWeight="bold">{option.label}</Typography>
              {(option.status || option.brand) && (
                <Typography color="text.secondary" variant="caption">
                  {option.status ? option.status : ''}
                  {option.status && option.brand ? ' · ' : ''}
                  {option.brand ? option.brand.toUpperCase() : ''}
                </Typography>
              )}
            </Box>
          </li>
        );
      }

      const cachedUser = userCache[option.value];
      const originalUser = option.user;

      const displayName =
        option.fullName ??
        originalUser?.fullName ??
        cachedUser?.fullName ??
        originalUser?.email ??
        cachedUser?.email ??
        '';

      const numberOfPurchases = cachedUser?.number_of_purchases || 0;

      const purchaseInfo = `${numberOfPurchases || 0} orders`;
      const lastLoginText = cachedUser?.lastLogin?.login_date
        ? `Last active ${dayjs(cachedUser.lastLogin.login_date).fromNow()}`
        : '';
      const luxPlusStatus = cachedUser?.subscriber_tier === 'base' ? 'LuxPlus+' : '';

      let extras = '';
      if (cachedUser) {
        const spacedPipe = ' \u00A0\u00A0| \u00A0\u00A0';
        extras = [purchaseInfo, luxPlusStatus, lastLoginText].filter(Boolean).join(spacedPipe);
      }

      const textColor = numberOfPurchases > 0 ? 'text.primary' : 'text.secondary';

      if (showOnlyEmail) {
        return (
          <li {...props} onMouseEnter={() => handleOptionHover(option)} style={{ cursor: 'pointer' }}>
            {originalUser?.email}
            {extras && (
              <Typography component="span" color={textColor} sx={{ ml: 1 }}>
                {extras}
              </Typography>
            )}
          </li>
        );
      }

      return (
        <li {...props} onMouseEnter={() => handleOptionHover(option)} style={{ cursor: 'pointer' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography fontWeight="bold" sx={{ minWidth: '150px', flexShrink: 0 }}>
              {displayName}
            </Typography>
            <Typography sx={{ minWidth: '250px', flexShrink: 0 }}>{originalUser?.email}</Typography>
          </Box>
          {extras && (
            <Typography component="span" color={textColor} sx={{ ml: 1 }}>
              {extras}
            </Typography>
          )}
        </li>
      );
    },
    [handleOptionHover, showOnlyEmail, userCache],
  );

  return (
    <FormControl error={formErrors?.includes(id)} sx={{ minWidth: 240 }} fullWidth>
      <Autocomplete
        id={id}
        clearOnBlur={clearOnBlur}
        multiple={false}
        freeSolo
        disabled={disabled}
        readOnly={readonly}
        options={options}
        loading={loading || isLoading || isOrdersLoading || isOffersLoading}
        loadingText="Looking through users, orders, and offers..."
        isOptionEqualToValue={(option, value) => option.entityType === value.entityType && option.value === value.value}
        filterOptions={(x) => x}
        noOptionsText="No matches found"
        onKeyUp={onEnterPress}
        onChange={(e, newOption) => {
          if (typeof newOption === 'string') {
            setInputValue(newOption);
            return;
          }

          if (!newOption) {
            onChange(id, '');
            if (setPassedAutocompleteValue) {
              setPassedAutocompleteValue('');
            }
            setInputValue('');
            onSelectResult?.(null);
            return;
          }

          handleOptionSelect(newOption);
        }}
        inputValue={inputValue}
        onInputChange={onInputChange}
        renderInput={(params) => (
          <TextField
            {...params}
            name={id}
            required={required}
            label={label}
            fullWidth
            data-testid="Search"
            error={formErrors?.includes(id)} // Added to highlight the errored field red.
            helperText={formErrors?.includes(id) ? 'Please select value from a dropdown' : undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading || isLoading || isOrdersLoading || isOffersLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        slotProps={{ paper: { elevation: 1 }, listbox: { component: 'ul', role: 'listbox' } }}
        renderOption={renderOption}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || option.value)}
      />
    </FormControl>
  );
}

export default UserSearchWidget;
